import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = (companyId) => ({
  WH_Id: undefined,
  WH_Code: "",
  WH_Name: "",
  WH_Status: "Y",
  ...(companyId ? { Comp_Id: companyId } : {}),
  Flag_Vansale: "N",
});

const WarehouseForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  masterList = [],
  setMode,
}) => {
  const companyId = useMemo(
    () => window.localStorage.getItem("company") || "",
    []
  );
  const [initialFormData, setInitialFormData] = useState(
    DEFAULT_FORM(companyId)
  );
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const hasCompIdField = useMemo(() => {
    if (selectedData && Object.prototype.hasOwnProperty.call(selectedData, "Comp_Id")) {
      return true;
    }
    if (Array.isArray(masterList) && masterList.length > 0) {
      return masterList.some((item) =>
        Object.prototype.hasOwnProperty.call(item || {}, "Comp_Id")
      );
    }
    return Boolean(companyId);
  }, [masterList, selectedData, companyId]);

  const handleBack = () => {
    if (typeof setMode === "function") {
      setMode("S");
    } else {
      window.location.href = "/tbsetwh";
    }
  };

  useEffect(() => {
    if (mode === "I") {
      const defaults = DEFAULT_FORM(companyId);
      setFormData(defaults);
      setInitialFormData(defaults);
    } else if (mode === "U" && selectedData) {
      const existingData = {
        WH_Id: selectedData.WH_Id,
        WH_Code: selectedData.WH_Code || "",
        WH_Name: selectedData.WH_Name || "",
        WH_Status: selectedData.WH_Status || "Y",
        ...(companyId || selectedData.Comp_Id
          ? { Comp_Id: companyId || selectedData.Comp_Id }
          : {}),
        Flag_Vansale: "N",
      };
      setFormData(existingData);
      setInitialFormData(existingData);
    }
  }, [mode, selectedData, hasCompIdField, companyId, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sanitizePayload = useCallback(
    (data) => {
      const payload = { ...data };

      if (payload.WH_Code) {
        payload.WH_Code = payload.WH_Code.trim();
      }
      payload.WH_Name = (payload.WH_Name || "").trim();
      payload.WH_Status = payload.WH_Status === "N" ? "N" : "Y";

      if (companyId) {
        payload.Comp_Id = companyId;
      }

      payload.Flag_Vansale = "N";

      if (!hasCompIdField) {
        delete payload.Comp_Id;
      }

      return payload;
    },
    [companyId, hasCompIdField]
  );

  const buildInsertArguments = (data) => {
    const prepared = { ...data };
    delete prepared.WH_Id;
    const entries = Object.entries(prepared).filter(([, v]) => v !== undefined);
    const fields = entries.map(([key]) => key).join(", ");
    const values = entries.map(([, value]) => value);
    return { fields, values };
  };

  const buildUpdateValues = (data) => {
    const prepared = { ...data };
    delete prepared.WH_Id;
    Object.keys(prepared).forEach((key) => {
      if (prepared[key] === undefined) {
        delete prepared[key];
      }
    });
    return prepared;
  };

  const handleInsert = useCallback(
    async (payload) => {
      const { fields, values } = buildInsertArguments(payload);
      const response = await insertData("Tb_Set_WH", fields, values);
      if (response?.status === "OK") {
        await callInitialize();
      } else {
        getAlert("FAILED", response?.message || "ไม่สามารถบันทึกข้อมูลได้");
      }
    },
    [callInitialize]
  );

  const handleUpdate = useCallback(
    async (payload) => {
      const where = { WH_Id: payload.WH_Id };
      const values = buildUpdateValues(payload);
      await updateData("Tb_Set_WH", values, where);
      await callInitialize();
    },
    [callInitialize]
  );

  const handleDelete = async () => {
    if (!selectedData?.WH_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (!isConfirm) return;
      try {
        const targetCompId =
          selectedData?.Comp_Id || companyId || localStorage.getItem("company");
        const whereConditions = [`WH_Id = ${selectedData.WH_Id}`];

        if (targetCompId) {
          whereConditions.push(`Comp_Id = '${targetCompId}'`);
        }

        await deleteData("Tb_Set_WH", `WHERE ${whereConditions.join(" AND ")}`);
        await callInitialize();
      } catch (error) {
        getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.WH_Name) {
      getAlert("WARNING", "โปรดกรอกชื่อคลังสินค้า");
      return;
    }

    const payload = sanitizePayload(formData);

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert(payload);
        const defaults = DEFAULT_FORM(companyId);
        setFormData(defaults);
        setInitialFormData(defaults);
      } else if (mode === "U") {
        await handleUpdate(payload);
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [
    formData,
    mode,
    handleInsert,
    handleUpdate,
    sanitizePayload,
    hasCompIdField,
    companyId,
    setFormData,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target?.tagName === "INPUT" ||
        event.target?.tagName === "TEXTAREA" ||
        event.target?.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/tbsetwh"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-warehouse" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ข้อมูลคลังสินค้า</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-6">
          <label style={labelStyle}>รหัสคลัง</label>
          <input
            type="text"
            className="form-control"
            name="WH_Code"
            value={formData.WH_Code || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-6">
          <label style={labelStyle}>ชื่อคลัง</label>
          <input
            type="text"
            className="form-control"
            name="WH_Name"
            value={formData.WH_Name || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        {hasCompIdField && (
          <div className="col-md-6">
            <label style={labelStyle}>บริษัท</label>
            <input
              type="text"
              className="form-control"
              value={companyId || formData.Comp_Id || ""}
              disabled
              style={{ ...inputStyle, backgroundColor: "#f3f4f6" }}
            />
          </div>
        )}
        <div className="col-md-6">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="WH_Status"
            value={formData.WH_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default WarehouseForm;
